import { get } from "lodash"
import { getExcelValue } from "helpers/csv"
import { GMV_SUPPORTED_TYPES, makeCurrencyExportLabel } from "helpers/comparison"
import { ERROR, OK } from "helpers/data-statuses"
import type { DataStatus } from "helpers/data-statuses"
import { CURRENCY_SCALES } from "helpers/formatters/numbers"
import { getCurrencyScaleAndFormatterForValues, makeScaledValueFormatter } from "helpers/formatters/currency-scale"
import type { ValueFormatter } from "helpers/formatters/currency-scale"
import { LOADING_CELL_TEXT, MISSING_VALUE_TEXT, NOT_APPLICABLE_TEXT } from "../constants"

const GROSS_MARKET_VALUE_LABEL = "Gross Market Value"
const MARKET_CAPITALIZATION_LABEL = "Market Capitalization"
const GROSS_MARKET_VALUE_PATH = "compositionSummary.gmv"
const MARKET_CAPITALIZATION_PATH = "compositionSummary.marketCapitalization"

export interface SummaryColumn {
  type?: string
  noData?: boolean
  isUnmodeled?: boolean
}

export interface SummaryDailyData {
  compositionSummary?: {
    gmv?: unknown
    marketCapitalization?: unknown
  }
}

type DailyDataEntry = SummaryDailyData | null | undefined

export interface RowUnit {
  currencyScale: string
  currency?: string | null
}

export interface SummaryRowInput {
  columns: readonly SummaryColumn[]
  detailsData: readonly DailyDataEntry[]
  statuses: readonly (DataStatus | undefined)[]
  currency?: string | null
}

export interface DollarSummaryRow {
  label: string
  exportLabel: string
  fn: (item: DailyDataEntry, type?: string) => string
  rawFn: (item: DailyDataEntry, type?: string) => ReturnType<typeof getExcelValue> | null
  unit?: RowUnit
}

interface RowDefinition {
  label: string
  path: string
  supportsType: (type: string | undefined) => boolean
}

function isGrossMarketValueType(type: string | undefined): boolean {
  return type !== undefined && GMV_SUPPORTED_TYPES.includes(type)
}

function isAnyType(): boolean {
  return true
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value)
}

function isStillLoading(dailyData: DailyDataEntry, status: DataStatus | undefined): boolean {
  return !dailyData && status !== OK && status !== ERROR
}

export function buildSummaryExportLabel(label: string, currency?: string | null): string {
  return makeCurrencyExportLabel(label, [{ currency }])
}

function buildRow(
  { label, path, supportsType }: RowDefinition,
  { columns, detailsData, statuses, currency }: SummaryRowInput,
  holdsForEveryColumn: boolean,
  resolveFormatter: (values: number[]) => { currencyScale: string, formatter: ValueFormatter },
): DollarSummaryRow {
  const readValue = (dailyData: DailyDataEntry): unknown => get(dailyData, path)
  const eligibleIndexes = columns.flatMap((column, index) =>
    supportsType(column.type) && !column.noData && !column.isUnmodeled ? [index] : [])
  const values = eligibleIndexes.map(index => readValue(detailsData[index])).filter(isFiniteNumber)
  const exportLabel = buildSummaryExportLabel(label, currency)
  const rawFn: DollarSummaryRow["rawFn"] = (item, type) => {
    const value = readValue(item)
    return supportsType(type) && isFiniteNumber(value) ? getExcelValue(value, { currency: true }) : null
  }

  const isAwaitingColumns = holdsForEveryColumn
    && eligibleIndexes.some(index => isStillLoading(detailsData[index], statuses[index]))

  if (isAwaitingColumns) {
    return {
      label,
      exportLabel,
      fn: (_item, type) => supportsType(type) ? LOADING_CELL_TEXT : NOT_APPLICABLE_TEXT,
      rawFn,
    }
  }

  if (values.length === 0) {
    return {
      label,
      exportLabel,
      fn: (_item, type) => supportsType(type) ? MISSING_VALUE_TEXT : NOT_APPLICABLE_TEXT,
      rawFn,
    }
  }

  const { currencyScale, formatter } = resolveFormatter(values)

  return {
    label,
    exportLabel,
    fn: (item, type) => {
      if (!supportsType(type)) return NOT_APPLICABLE_TEXT
      const value = readValue(item)
      return isFiniteNumber(value) ? formatter(value) : MISSING_VALUE_TEXT
    },
    rawFn,
    unit: { currencyScale, currency },
  }
}

export function buildGrossMarketValueRow(input: SummaryRowInput): DollarSummaryRow {
  return buildRow(
    { label: GROSS_MARKET_VALUE_LABEL, path: GROSS_MARKET_VALUE_PATH, supportsType: isGrossMarketValueType },
    input,
    true,
    getCurrencyScaleAndFormatterForValues,
  )
}

export function buildMarketCapitalizationRow(input: SummaryRowInput): DollarSummaryRow {
  const row = buildRow(
    { label: MARKET_CAPITALIZATION_LABEL, path: MARKET_CAPITALIZATION_PATH, supportsType: isAnyType },
    input,
    false,
    () => ({
      currencyScale: CURRENCY_SCALES.M,
      formatter: makeScaledValueFormatter(CURRENCY_SCALES.M),
    }),
  )
  return {
    ...row,
    fn: item => row.fn(item),
    rawFn: item => row.rawFn(item),
  }
}
